import { useCallback, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { X } from "lucide-react"
import { routes } from "@router/routes"
import ReddyShell from "./components/reddy-shell"
import RedButton from "./components/red-button"
import { useReddyLanguage } from "./context/reddy-language.context"
import { useReddyVoice } from "./hooks/useReddyVoice"
import { reddyStore } from "./store/reddy-store"
import { ask } from "./api/reddy-client"

export default function ReddyDrivingScreen() {
  return (
    <ReddyShell>
      <DrivingContent />
    </ReddyShell>
  )
}

function DrivingContent() {
  const { tx, language } = useReddyLanguage()
  const navigate = useNavigate()
  const [active, setActive] = useState(false)
  const [pending, setPending] = useState(false)
  const [lastSummary, setLastSummary] = useState("")
  const [heard, setHeard] = useState("")
  const activeRef = useRef(false)

  const handleFinal = useCallback(
    async (text: string) => {
      setHeard(text)
      reddyStore.addUserMessage(text)
      setPending(true)
      try {
        const result = await ask({ text, language })
        reddyStore.addAssistantMessage(result)
        reddyStore.recordHistory(text, result)
        // Voice-only summary: spoken answer + the single best option.
        const best = result.results[0]
        const summary = best
          ? `${result.answer} ${
              language === "he" ? "ההמלצה הראשונה:" : "Top pick:"
            } ${best.title}${best.price ? ` — ${best.price}` : ""}`
          : result.answer
        setLastSummary(summary)
        await voice.speak(summary)
      } catch {
        const msg =
          language === "he"
            ? "לא הצלחתי כרגע. ננסה שוב?"
            : "Couldn't do that now. Try again?"
        setLastSummary(msg)
        voice.speak(msg)
      } finally {
        setPending(false)
        // Hands-free: resume listening for the next request.
        if (activeRef.current) {
          setTimeout(() => voice.startListening(), 600)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  )

  const voice = useReddyVoice({ language, onFinal: handleFinal })

  const toggle = () => {
    if (active) {
      activeRef.current = false
      setActive(false)
      voice.stopListening()
      voice.stopSpeaking()
    } else {
      activeRef.current = true
      setActive(true)
      voice.startListening()
    }
  }

  const state = voice.isListening
    ? "listening"
    : pending || voice.ttsState !== "idle"
      ? "thinking"
      : "idle"

  return (
    <div className="flex h-full flex-col bg-[#1A1A1A] text-white">
      <div className="flex items-center justify-between px-5 py-4">
        <span className="text-lg font-extrabold">{tx.drivingTitle}</span>
        <button
          type="button"
          onClick={() => navigate(routes.reddy.root)}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <div className="scale-125">
          <RedButton
            state={state}
            label={
              active
                ? voice.isListening
                  ? tx.buttonListening
                  : tx.buttonThinking
                : tx.buttonIdle
            }
            onPress={toggle}
          />
        </div>

        <p className="mt-10 text-base text-white/70">{tx.drivingHint}</p>

        {heard && !voice.isListening && (
          <p className="mt-6 text-sm text-white/60">“{heard}”</p>
        )}
        {voice.isListening && (
          <p className="mt-6 min-h-[28px] text-xl font-bold text-white">
            {voice.interim}
          </p>
        )}
        {lastSummary && (
          <p className="mt-4 max-w-[300px] text-[15px] font-medium text-white">
            {lastSummary}
          </p>
        )}
        {!voice.isSttSupported && (
          <p className="mt-6 text-xs text-white/40">{tx.micUnsupported}</p>
        )}
      </div>

      {active && (
        <button
          type="button"
          onClick={toggle}
          className="mx-6 mb-8 rounded-full bg-white/10 py-3 text-sm font-semibold"
        >
          {tx.drivingStop}
        </button>
      )}
    </div>
  )
}
